// src/components/dashboard/CouponTab.jsx
import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Heading,
    Text,
    VStack,
    HStack,
    FormControl,
    FormLabel,
    Input,
    InputGroup,
    Divider,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    SimpleGrid,
    Flex,
    useToast,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    FormHelperText,
    Wrap,
    WrapItem,
} from '@chakra-ui/react';
import {
    FiCopy,
    FiCheck,
    FiShare2,
    FiDollarSign,
    FiEdit,
} from 'react-icons/fi';
import { supabase } from '../../config/supabaseClient';

const CouponTab = ({ profile }) => {
    const [copied, setCopied] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [newCouponCode, setNewCouponCode] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [totalUses, setTotalUses] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const toast = useToast();

    // Buscar estatísticas do cupom ao carregar o componente
    useEffect(() => {
        const fetchCouponStats = async () => {
            if (!profile?.id) return;
            
            setIsLoading(true);
            
            try {
                // Buscar contagem de usos do cupom
                const { count, error } = await supabase
                    .from('cupom_usage')
                    .select('*', { count: 'exact' })
                    .eq('dono_id', profile.id);
                
                if (error) throw error;
                
                setTotalUses(count || 0);
            } catch (error) {
                console.error('Erro ao buscar estatísticas do cupom:', error);
                toast({
                    title: 'Erro',
                    description: 'Não foi possível carregar as estatísticas do cupom.',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
            } finally {
                setIsLoading(false);
            }
        };
        
        fetchCouponStats();
    }, [profile?.id, toast]);

    const handleCopyCupom = () => {
        if (profile?.codigo_cupom) {
            navigator.clipboard.writeText(profile.codigo_cupom);
            setCopied(true);

            toast({
                title: 'Código copiado!',
                description: 'O código de cupom foi copiado para a área de transferência.',
                status: 'success',
                duration: 2000,
                isClosable: true,
            });

            setTimeout(() => setCopied(false), 2000);
        }
    };
    
    const handleEditCouponOpen = () => {
        setNewCouponCode(profile?.codigo_cupom || '');
        setIsEditModalOpen(true);
    };
    
    const handleEditCouponClose = () => {
        setIsEditModalOpen(false);
    };
    
    const handleSaveCouponCode = async () => {
        if (!newCouponCode.trim()) {
            toast({
                title: 'Código inválido',
                description: 'Por favor, insira um código de cupom válido.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return;
        }
        
        setIsSaving(true);
        
        try {
            // Verificar se o código já existe
            const { count, error: countError } = await supabase
                .from('profiles')
                .select('*', { count: 'exact' })
                .eq('codigo_cupom', newCouponCode.toUpperCase())
                .neq('id', profile.id); // Excluir o próprio usuário da verificação
            
            if (countError) throw countError;
            
            if (count > 0) {
                toast({
                    title: 'Código já em uso',
                    description: 'Este código de cupom já está sendo usado por outro usuário. Por favor, escolha um código diferente.',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
                return;
            }
            
            // Atualizar o código do cupom
            const { error: updateError } = await supabase
                .from('profiles')
                .update({ codigo_cupom: newCouponCode.toUpperCase() })
                .eq('id', profile.id);
            
            if (updateError) {
                // Verificar se é erro de duplicação
                if (updateError.code === '23505') {
                    toast({
                        title: 'Código já em uso',
                        description: 'Este código de cupom já está sendo usado por outro usuário. Por favor, escolha um código diferente.',
                        status: 'error',
                        duration: 3000,
                        isClosable: true,
                    });
                    return;
                }
                throw updateError;
            }
            
            toast({
                title: 'Código atualizado',
                description: 'Seu código de cupom foi atualizado com sucesso.',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            
            // Atualizar o perfil na página
            window.location.reload();
            
            // Fechar o modal
            handleEditCouponClose();
        } catch (error) {
            console.error('Erro ao atualizar código de cupom:', error);
            
            // Verificar se é erro de duplicação
            if (error.code === '23505') {
                toast({
                    title: 'Código já em uso',
                    description: 'Este código de cupom já está sendo usado por outro usuário. Por favor, escolha um código diferente.',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
            } else {
                toast({
                    title: 'Erro',
                    description: 'Ocorreu um erro ao atualizar seu código de cupom.',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
            }
        } finally {
            setIsSaving(false);
        }
    };
    
    // Compartilhar nas redes sociais
    const websiteUrl = window.location.origin;
    const discountAmount = "15%";
    const shareText = `Transforme suas fotos em desenhos para colorir! Use meu código "${profile?.codigo_cupom}" no Draw Your Goods e ganhe ${discountAmount} de desconto na sua primeira compra. Ideal para crianças e adultos que adoram arte! Acesse agora: ${websiteUrl}?ref=${profile?.codigo_cupom}`;
    
    const handleShareFacebook = () => {
        const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(websiteUrl)}&quote=${encodeURIComponent(shareText)}`;
        window.open(url, '_blank');
    };
    
    const handleShareTwitter = () => {
        const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;
        window.open(url, '_blank');
    };
    
    const handleShareWhatsApp = () => {
        const url = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
        window.open(url, '_blank');
    };

    return (
        <Box>
            <Heading size="lg" color="gray.700" mb={6}>
                Seu código de cupom
            </Heading>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 4, md: 8 }}>
                <Box bg="white" p={{ base: 4, md: 6 }} borderRadius="lg" boxShadow="md">
                    <VStack spacing={{ base: 4, md: 6 }} align="stretch">
                        <Text fontSize={{ base: "md", md: "lg" }} color="gray.700">
                            Compartilhe seu código personalizado e ganhe 15% do valor da compra
                            quando alguém utilizar seu cupom.
                        </Text>

                        <Box p={{ base: 3, md: 4 }} bg="gray.50" borderRadius="md">
                            <FormControl>
                                <FormLabel fontWeight="medium">Seu código exclusivo:</FormLabel>
                                <Flex 
                                    direction={{ base: "column", sm: "row" }}
                                    gap={{ base: 2, sm: 0 }}
                                >
                                    <Input
                                        value={profile?.codigo_cupom}
                                        isReadOnly
                                        bg="white"
                                        fontWeight="bold"
                                        letterSpacing="wider"
                                        borderRightRadius={{ base: "md", sm: 0 }}
                                        mb={{ base: 2, sm: 0 }}
                                    />
                                    <Flex>
                                        <Button
                                            onClick={handleCopyCupom}
                                            colorScheme={copied ? "green" : "blue"}
                                            leftIcon={copied ? <FiCheck /> : <FiCopy />}
                                            borderRadius={{ base: "md", sm: "0" }}
                                        >
                                            {copied ? "Copiado!" : "Copiar"}
                                        </Button>
                                        <Button
                                            onClick={handleEditCouponOpen}
                                            colorScheme="purple"
                                            leftIcon={<FiEdit />}
                                            ml={2}
                                            borderLeftRadius={{ sm: "0" }}
                                        >
                                            Editar
                                        </Button>
                                    </Flex>
                                </Flex>
                            </FormControl>
                        </Box>

                        <Divider />

                        <VStack align="start" spacing={4}>
                            <Heading size="md" color="gray.700">
                                Compartilhe nas redes sociais
                            </Heading>
                            <Wrap spacing={{ base: 2, md: 4 }}>
                                <WrapItem>
                                    <Button 
                                        colorScheme="facebook" 
                                        leftIcon={<FiShare2 />}
                                        onClick={handleShareFacebook}
                                        size={{ base: "sm", md: "md" }}
                                    >
                                        Facebook
                                    </Button>
                                </WrapItem>
                                <WrapItem>
                                    <Button 
                                        colorScheme="twitter" 
                                        leftIcon={<FiShare2 />}
                                        onClick={handleShareTwitter}
                                        size={{ base: "sm", md: "md" }}
                                    >
                                        Twitter
                                    </Button>
                                </WrapItem>
                                <WrapItem>
                                    <Button 
                                        colorScheme="whatsapp" 
                                        leftIcon={<FiShare2 />}
                                        onClick={handleShareWhatsApp}
                                        size={{ base: "sm", md: "md" }}
                                    >
                                        WhatsApp
                                    </Button>
                                </WrapItem>
                            </Wrap>
                        </VStack>
                    </VStack>
                </Box>

                <Box bg="white" p={{ base: 4, md: 6 }} borderRadius="lg" boxShadow="md">
                    <VStack spacing={{ base: 4, md: 6 }} align="stretch">
                        <Heading size="md" color="gray.700">
                            Resumo de desempenho do seu cupom
                        </Heading>

                        <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
                            <Stat>
                                <StatLabel>Total de Usos</StatLabel>
                                <StatNumber>{isLoading ? '...' : totalUses}</StatNumber>
                                <StatHelpText>Desde o início</StatHelpText>
                            </Stat>

                            <Stat>
                                <StatLabel>Comissão Total</StatLabel>
                                <StatNumber>R$ {(profile?.comissao_total || 0).toFixed(2)}</StatNumber>
                                <StatHelpText>Acumulada</StatHelpText>
                            </Stat>
                        </SimpleGrid>

                        <Box p={{ base: 3, md: 4 }} bg="gray.50" borderRadius="md">
                            <Flex 
                                justify="space-between" 
                                align="center"
                                direction={{ base: "column", sm: "row" }}
                                gap={{ base: 3, sm: 0 }}
                            >
                                <VStack align={{ base: "center", sm: "start" }} spacing={1}>
                                    <Text fontWeight="medium">Comissão disponível:</Text>
                                    <Heading size="md" color="green.500">
                                        R$ {(profile?.comissao_disponivel || 0).toFixed(2)}
                                    </Heading>
                                </VStack>

                                <Button
                                    leftIcon={<FiDollarSign />}
                                    colorScheme="green"
                                    isDisabled={!profile?.comissao_disponivel || profile.comissao_disponivel <= 0}
                                    width={{ base: "full", sm: "auto" }}
                                >
                                    Solicitar saque
                                </Button>
                            </Flex>
                        </Box>
                    </VStack>
                </Box>
            </SimpleGrid>
            
            {/* Modal para editar o código de cupom */}
            <Modal isOpen={isEditModalOpen} onClose={handleEditCouponClose}>
                <ModalOverlay />
                <ModalContent mx={4}>
                    <ModalHeader>Editar código de cupom</ModalHeader>
                    <ModalCloseButton />
                    
                    <ModalBody>
                        <FormControl>
                            <FormLabel>Novo código de cupom</FormLabel>
                            <Input 
                                value={newCouponCode}
                                onChange={(e) => setNewCouponCode(e.target.value.toUpperCase())}
                                placeholder="Digite seu novo código"
                                maxLength={8}
                            />
                            <FormHelperText>
                                O código deve ter no máximo 8 caracteres e ser único no sistema.
                            </FormHelperText>
                        </FormControl>
                    </ModalBody>
                    
                    <ModalFooter>
                        <Button 
                            colorScheme="blue" 
                            mr={3} 
                            onClick={handleSaveCouponCode}
                            isLoading={isSaving}
                        >
                            Salvar
                        </Button>
                        <Button variant="ghost" onClick={handleEditCouponClose}>
                            Cancelar
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            
            {/* Exemplo de como ficaria a mensagem compartilhada no WhatsApp 
            <Box 
                mt={8} 
                p={5} 
                bg="white" 
                borderRadius="lg" 
                boxShadow="md"
                borderLeft="5px solid #25D366"
            >
                <Heading size="md" mb={4} color="gray.700">
                    Exemplo de mensagem compartilhada no WhatsApp:
                </Heading>
                <Box 
                    p={4} 
                    bg="#E2F7CB" 
                    borderRadius="lg" 
                    fontSize={{ base: "sm", md: "md" }}
                >
                    <Text>
                        🎨 Transforme suas fotos em desenhos para colorir! Use meu código "<strong>{profile?.codigo_cupom}</strong>" no Draw Your Goods e ganhe {discountAmount} de desconto na sua primeira compra. Ideal para crianças e adultos que adoram arte! 🖌️
                    </Text>
                    <Text mt={2} color="blue.600" textDecoration="underline">
                        {websiteUrl}?ref={profile?.codigo_cupom}
                    </Text>
                </Box>
                <Text mt={3} fontSize="sm" color="gray.600">
                    Esta mensagem atraente destaca o benefício principal (transformar fotos em desenhos), 
                    oferece um incentivo claro (15% de desconto) e inclui emojis relevantes para capturar a atenção.
                    O link personalizado com seu código permite rastrear quantas pessoas clicaram!
                </Text>
            </Box>*/}
        </Box>
    );
};

export default CouponTab;